import type { FlattenedComparison } from "#shared/comparisonSelection.ts";
import type { PeriodSelection, ReviewHistoryFilter } from "#shared/periodSelection.ts";

export const BASE_SERIES_ID = "base";

const BASE_SWATCH_CLASSNAME = "bg-primary";

const COMPARISON_SWATCH_CLASSNAMES = ["bg-series-1", "bg-series-2", "bg-series-3", "bg-series-4", "bg-series-5", "bg-series-6", "bg-series-7", "bg-series-8", "bg-series-9"] as const;

const LETTER_A = 65;

function toTopLevelLetter(position: number): string {
    return String.fromCharCode(LETTER_A + position);
}

export const BASE_SERIES_LETTER = toTopLevelLetter(0);

export function assignSeriesLetters(entries: FlattenedComparison[]): string[] {
    const lettersById = new Map<string, string>([[BASE_SERIES_ID, BASE_SERIES_LETTER]]);
    const groupCounts = new Map<string, number>();
    let nextTopLevel = 1;

    return entries.map(entry => {
        if (entry.kind === "comparison") {
            const letter = toTopLevelLetter(nextTopLevel);
            nextTopLevel += 1;
            lettersById.set(entry.id, letter);

            return letter;
        }

        const ordinal = (groupCounts.get(entry.parentId) ?? 0) + 1;
        groupCounts.set(entry.parentId, ordinal);

        return `${lettersById.get(entry.parentId) ?? ""}${ordinal}`;
    });
}

export function toSwatchClassName(position: number): string {
    if (position <= 0) {
        return BASE_SWATCH_CLASSNAME;
    }

    return COMPARISON_SWATCH_CLASSNAMES[(position - 1) % COMPARISON_SWATCH_CLASSNAMES.length];
}

export type PeriodDescriptor = { kind: "cycles"; count: number } | { kind: "preset"; preset: ReviewHistoryFilter } | { kind: "range"; from?: string; to?: string } | { kind: "all" };

export function describePeriod(period: PeriodSelection): PeriodDescriptor {
    if (period.reviewCycleIds?.length) {
        return { kind: "cycles", count: period.reviewCycleIds.length };
    }

    if (period.reviewHistoryFilter) {
        return { kind: "preset", preset: period.reviewHistoryFilter };
    }

    if (period.from || period.to) {
        return { kind: "range", from: period.from, to: period.to };
    }

    return { kind: "all" };
}
